'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/side-panel/styles.css';
import { IconButton } from 'BomdisoftComponents/buttons/icon';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { CloseRoundedIcon } from 'BomdisoftComponents/icons/index';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import type { CSSProperties, ReactNode, Ref } from 'react';

export type { SidePanelPlacement } from 'BomdisoftComponents/side-panel/render';

type SidePanelBase = {
  /** Controls that belong beside the close: an action menu, an overflow, a
   *  pin. They sit before it, so the way out keeps the far end to itself. */
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Translated, and used for both the tooltip and the accessible name, so the
   *  close control never depends on the glyph to say what it does. */
  closeLabel: string;
  /** A direct child of the card, outside the scrolling body: a resize grip on
   *  the card's own edge. The body clips, so a grip that hangs past the border
   *  cannot live in it. */
  edge?: ReactNode;
  /** A region pinned under the body and above the footer, across the card's
   *  whole width: a live preview of what the fields write. It scrolls on its own,
   *  so the fields above can scroll without moving it. */
  dock?: ReactNode;
  /** A row pinned under the body: the Save of a panel that writes. It never
   *  scrolls with the fields. */
  footer?: ReactNode;
  /** Decoration before the title: a monogram, an avatar, an entity mark. Passed
   *  through untouched, because what identifies a person is not what identifies
   *  a webhook and neither is this component's business. */
  lead?: ReactNode;
  /** The secondary line under the title: an email, an identifier, a kind. */
  meta?: ReactNode;
  onClose: () => void;
  /** The card element itself, for a page that has to bring it into view: on a
   *  phone the card stands under the grid that opened it, and a reader who
   *  tapped a row at the top of a hundred cannot otherwise tell it opened. */
  ref?: Ref<HTMLElement>;
  testId?: string;
  title: ReactNode;
};

export type SidePanelProps = SidePanelBase &
  (
    | {
        /** Seated in the page's own row beside the grid. The row supplies the
         *  gap; the card supplies everything else. */
        placement?: 'flow';
        open?: never;
        width?: number;
      }
    | {
        /** Inside a sheet that already draws a surface and supplies a way out.
         *  Two borders and two ways out is one of each too many. */
        placement: 'bare';
        open?: never;
        width?: never;
      }
    | {
        /** Opened from a row into a page that did not leave a column for it.
         *  The holder grows from nothing to the card's width, so the content
         *  beside it moves over and the card lands where a seated one would:
         *  in the page's own row, under its head. */
        placement: 'docked';
        open: boolean;
        width: number;
      }
  );

/**
 * The surface a data grid opens beside itself.
 *
 * **It is a contained box, never a flush slab.** Border on all four sides,
 * `radius-md`, paper over the page, and inset from whatever it sits in. A panel
 * that runs edge to edge with one keyline reads as part of the page frame, so
 * nothing on screen says the row opened it, and two of them in one app drift to
 * two widths of the same idea. Every console and admin grid draws this one.
 *
 * **The placement is the caller's, because the three are different elements
 * rather than one element in three positions.** `flow` is seated in a row that
 * already has a column for it; `docked` brings its own column, growing it from
 * nothing so the content beside it moves over; `bare` drops the card for a
 * mobile sheet.
 *
 * **No placement floats over the page.** A card pinned to the viewport covers
 * the head that names what it is about, which is what the slab it replaced did
 * and the reason it read as page furniture rather than as something a row
 * opened.
 */
export const SidePanel = ({ actions, children, className, closeLabel, dock, edge, footer, lead, meta, onClose, open, placement = 'flow', ref, testId, title, width }: SidePanelProps): ReactNode => {
  const sizing = width === undefined ? undefined : ({ '--side-panel-width': `${width}px` } as CSSProperties);

  const card = (
    <aside className={joinClasses('bomdisoft-side-panel', className)} data-placement={placement} data-side-panel-sizer={placement === 'flow' ? '' : undefined} data-testid={testId} ref={ref} style={placement === 'docked' ? undefined : sizing}>
      <div className='bomdisoft-side-panel-head'>
        {lead}
        <span className='bomdisoft-side-panel-who'>
          <span className='bomdisoft-side-panel-title'>{title}</span>
          {meta === undefined ? null : (
            <span className='bomdisoft-side-panel-meta' data-testid={testId === undefined ? undefined : `${testId}-meta`}>
              {meta}
            </span>
          )}
        </span>
        <span className='bomdisoft-side-panel-spacer' />
        {actions === undefined ? null : <span className='bomdisoft-side-panel-actions'>{actions}</span>}
        {placement === 'bare' ? null : (
          <Tooltip placement='left' title={closeLabel}>
            <IconButton aria-label={closeLabel} data-testid={testId === undefined ? undefined : `${testId}-close`} onClick={onClose} size='small'>
              <CloseRoundedIcon size={18} />
            </IconButton>
          </Tooltip>
        )}
      </div>
      <div className='bomdisoft-side-panel-body'>{children}</div>
      {dock === undefined ? null : (
        <div className='bomdisoft-side-panel-dock' data-testid={testId === undefined ? undefined : `${testId}-dock`}>
          {dock}
        </div>
      )}
      {footer === undefined ? null : (
        <div className='bomdisoft-side-panel-footer' data-testid={testId === undefined ? undefined : `${testId}-footer`}>
          {footer}
        </div>
      )}
      {edge}
    </aside>
  );

  if (placement !== 'docked') return card;

  return (
    <div className='bomdisoft-side-panel-holder' data-open={open ? '' : undefined} data-side-panel-sizer='' style={sizing}>
      {card}
    </div>
  );
};
